"use client";

import type { TGetListingProductsResponseDTO } from "@/app/api/listing/[slug]/products/listing-products.dto";
import { PRODUCTS_API } from "@/lib/constants";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import GridViewProductCard from "@/modules/listing/tabs/products-tab/products-feed/cards/grid-view-product-card";
import { Button } from "@/components/new/button";
import Link from "next/link";
import useCommunityAndChannelParams from "@/hooks/useChannelAndCommunityParams";
import { usePathname } from "next/navigation";
import ProductsTabSearchAndFilter from "./search-and-filter";
import { useAuth } from "@/providers/auth-provider";
import { useCountryContext } from "@/providers/country-provider";
import { cn } from "@/lib/utils";

async function fetchProducts({
	pageParam = 1,
	perPageParam = 10,
	hqLocation,
}: {
	pageParam: number;
	perPageParam?: number;
	hqLocation?: string[];
}) {
	try {
		const url = new URL(PRODUCTS_API);
		url.searchParams.set("page", pageParam.toString());
		url.searchParams.set("perPage", perPageParam.toString());

		if (hqLocation?.length) {
			url.searchParams.set("hqLocation", hqLocation.join(","));
		}

		const response = await fetch(url);
		if (!response.ok) {
			return {
				data: [],
			};
		}

		const { data, pagination } = (await response.json()) as {
			data: TGetListingProductsResponseDTO;
			pagination: { page?: string; perPage: string };
		};

		return {
			data,
			pagination,
		};
	} catch (error) {
		return {
			data: [],
		};
	}
}

const ProductsFeed = (props: { disableSearchFilter?: boolean }) => {
	const { isLoggedIn } = useAuth();
	const pathname = usePathname();
	const { communitySlug } = useCommunityAndChannelParams();
	const { products: countryProducts, country, name } = useCountryContext();
	const perPage = countryProducts.perPage;

	const [results, setResults] = useState<TGetListingProductsResponseDTO>([]);

	const {
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		isLoading,
		data: productsData,
	} = useInfiniteQuery({
		queryKey: ["products", country],
		queryFn: ({ pageParam }) =>
			fetchProducts({
				pageParam,
				perPageParam: perPage,
				hqLocation: country,
			}),
		getNextPageParam: (lastPage) =>
			lastPage.pagination?.page ? Number.parseInt(lastPage.pagination?.page) + 1 : undefined,
	});

	const products: TGetListingProductsResponseDTO = useMemo(() => {
		const formattedProducts: TGetListingProductsResponseDTO = [];
		productsData?.pages?.forEach((page) => {
			if (page?.data) {
				page?.data?.forEach((product) => formattedProducts.push(product));
			}
		});

		return formattedProducts;
	}, [productsData?.pages]);

	useEffect(() => {
		if (products.length > 0) {
			setResults(products);
		}
	}, [products]);

	return (
		<div>
			<div className={cn(!isLoggedIn ? "relative min-h-[600px]" : "")}>
			<h1 className="text-left text-[28px] font-bold text-[#0056E8] mb-3">{countryProducts.code ? countryProducts.code : name} Proptech Products</h1>
				<div>
					{!props.disableSearchFilter && (
						<ProductsTabSearchAndFilter
							initialResults={products}
							setResults={setResults}
							isLoading={isLoading}
						/>
					)}

					<div className="mx-auto my-6 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8 xl:grid-cols-3">
						{pathname === `/communities/${communitySlug}/products`
							? results.map((product) => <GridViewProductCard key={product.id} {...product} />)
							: results
									.slice(0, 9)
									.map((product) => <GridViewProductCard key={product.id} {...product} />)}
					</div>
				</div>

				{pathname !== `/communities/${communitySlug}/products` && (
					<div className="mt-6 flex justify-center">
						<Button className="rounded-[10px] border border-gray-300 bg-[#005EFF] px-4 py-3 text-[17px] text-white">
							<Link href={`/communities/${communitySlug}/products`}>Show more</Link>
						</Button>
					</div>
				)}

				<div className="mt-8">
					{!isLoading && !hasNextPage ? (
						<div className="flex items-center justify-center gap-x-1 text-sm font-medium text-gray-400">
							<span>You have reached the end.</span>
							<button
								className="underline"
								onClick={() => window?.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
							>
								back to top
							</button>
						</div>
					) : !isLoading && results.length >= perPage ? (
						<button
							onClick={() => fetchNextPage()}
							className="mx-auto block max-h-14 w-fit rounded-lg bg-white px-6 py-3 text-center text-sm font-medium text-gray-900 disabled:opacity-40 md:text-lg"
							disabled={isFetchingNextPage}
						>
							{isFetchingNextPage ? (
								<Loader2Icon className="h-5 w-5 animate-spin stroke-brand-primary" />
							) : (
								"Show more"
							)}
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default ProductsFeed;
